import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, ArrowRight, Settings, CheckCircle2, AlertTriangle, Clock } from "lucide-react";

const PLAN_LABEL = {
  founder_monthly: "Founding Member · $49/month",
  standard_monthly: "Standard · $79/month",
};

export default function BillingPage() {
  const navigate = useNavigate();
  const { authAxios } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [opening, setOpening] = useState(false);

  const load = () => {
    return authAxios.get("/billing/status")
      .then(r => setStatus(r.data))
      .catch(() => toast.error("Couldn't load billing info"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // If we came back from a Stripe checkout, refresh after a short delay (webhook should land)
    const params = new URLSearchParams(window.location.search);
    if (params.get("session_id")) {
      toast.success("Welcome aboard! Your 14-day free trial has started.");
      setTimeout(load, 1500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openPortal = async () => {
    setOpening(true);
    try {
      const res = await authAxios.post("/billing/portal", {
        plan: "standard_monthly", // ignored server-side; portal doesn't care
        origin_url: window.location.origin,
      });
      window.location.href = res.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't open billing portal");
      setOpening(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 lg:p-10" data-testid="billing-page">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      </div>
    );
  }

  const subStatus = status?.subscription_status || "none";
  const isTrialing = subStatus === "trialing";
  const isActive = subStatus === "active";
  const isPastDue = subStatus === "past_due";
  const isCanceled = subStatus === "canceled" || subStatus === "none";

  return (
    <div className="p-6 lg:p-10 max-w-3xl" data-testid="billing-page">
      <div className="mb-6">
        <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight" style={{fontFamily: "Manrope"}}>Billing</h1>
        <p className="text-slate-500 mt-1">Your subscription and payment method.</p>
      </div>

      {/* Status card */}
      <Card className="bg-white border-slate-200 rounded-2xl shadow-sm mb-6">
        <CardContent className="p-6">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Current Plan</div>
              <div className="text-xl font-bold text-slate-900" style={{fontFamily: "Manrope"}}>
                {PLAN_LABEL[status?.subscription_plan] || "No active subscription"}
              </div>
            </div>
            {isTrialing && <Badge className="bg-[#B7EB1B] text-[#0a1e3a] font-bold">Free Trial</Badge>}
            {isActive && <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200"><CheckCircle2 className="w-3 h-3 mr-1 inline" /> Active</Badge>}
            {isPastDue && <Badge className="bg-amber-100 text-amber-700 border-amber-200"><AlertTriangle className="w-3 h-3 mr-1 inline" /> Past Due</Badge>}
            {isCanceled && <Badge className="bg-slate-100 text-slate-600 border-slate-200">Inactive</Badge>}
          </div>

          {isTrialing && status?.trial_days_remaining !== null && (
            <div className="rounded-xl bg-[#B7EB1B]/10 border border-[#B7EB1B]/30 p-4 mb-4">
              <div className="flex items-center gap-2 text-sm font-semibold text-[#0a1e3a]">
                <Clock className="w-4 h-4" />
                {status.trial_days_remaining === 0
                  ? "Your free trial ends today."
                  : `${status.trial_days_remaining} day${status.trial_days_remaining === 1 ? '' : 's'} left in your free trial.`}
              </div>
              {!status.has_payment_method && (
                <p className="text-xs text-slate-600 mt-1">
                  Add a payment method to keep sending after the trial ends.
                </p>
              )}
            </div>
          )}

          {status?.subscription_ends_at && (isActive || isTrialing) && (
            <p className="text-sm text-slate-600">
              {isTrialing ? "Trial ends" : "Next billing date"}: <span className="font-semibold text-slate-900">
                {new Date(status.trial_ends_at || status.subscription_ends_at).toLocaleDateString(undefined, {month: 'long', day: 'numeric', year: 'numeric'})}
              </span>
            </p>
          )}

          <div className="mt-5 flex flex-wrap gap-2">
            {isCanceled ? (
              <Button
                onClick={() => navigate("/pricing")}
                data-testid="billing-start-cta"
                className="bg-[#B7EB1B] hover:bg-[#B7EB1B]/90 text-[#0a1e3a] rounded-full font-bold"
              >
                Start free trial <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            ) : (
              <Button
                onClick={openPortal}
                disabled={opening}
                data-testid="billing-portal-cta"
                className="bg-slate-900 hover:bg-slate-800 text-white rounded-full font-semibold"
              >
                {opening ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Opening…</> : <><Settings className="w-4 h-4 mr-2" /> Manage Subscription</>}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white border-slate-200 rounded-2xl shadow-sm">
        <CardContent className="p-6 text-sm text-slate-600">
          <p className="font-semibold text-slate-900 mb-2" style={{fontFamily: "Manrope"}}>Questions about your bill?</p>
          <p>All billing is handled through Stripe. Use <strong>Manage Subscription</strong> above to update your card, download invoices, change plan, or cancel.</p>
          <p className="mt-2">Anything else? Email <a href="mailto:info@anneflinn.com" className="text-[#0d9488] font-semibold hover:underline">info@anneflinn.com</a>.</p>
        </CardContent>
      </Card>
    </div>
  );
}
